import api from '@api';
import { withArchiveMode } from 'src/utils/archiveMode';
import { all, call, put, takeEvery, takeLatest } from 'redux-saga/effects';
import {
    getTransportDetailFetchSuccess,
    getTransportDetailFetchFailure,
    getTransportDetailChangelogFetchSuccess,
    getTransportDetailChangelogFetchFailure,
    createTransportDetailFetchSuccess,
    createTransportDetailFetchFailure,
    updateTransportDetailFetchSuccess,
    updateTransportDetailFetchFailure,
    getTransportPackageTypesFetchSuccess,
    getTransportPackageTypesFetchFailure,
    orderTransportShipmentFetchSuccess,
    orderTransportShipmentFetchFailure,
    getTransportApiCallsFetchSuccess,
    getTransportApiCallsFetchFailure,
    getTransportEventsFetchSuccess,
    getTransportEventsFetchFailure,
} from './reducer';
import { TransportType } from 'src/types/transport';
import translate from '@translate';
import {
    TransportApiCallType,
    TransportDetailResType,
    TransportEventType,
    TransportPackageTypeApiRow,
} from 'src/types/transportDetail';
import { ConstantsCodeType } from 'src/types/constantsCodes';

type PickupDatePayload = {
    shipper: string;
    hint?: string | null;
    callback: (pickupDate: string | null) => void;
};
const getTransportDetailFetchRequest = async (idTransport: string) => {
    const res = await api('get', withArchiveMode(`api/secure/transports/${idTransport}`));
    return res.data;
};

function* getTransportDetailFetch(action: { type: string; payload: string }) {
    try {
        const response: TransportDetailResType = yield call((): Promise<TransportDetailResType> =>
            getTransportDetailFetchRequest(action.payload)
        );
        yield put(getTransportDetailFetchSuccess(response));
    } catch (error) {
        yield put(getTransportDetailFetchFailure({ error }));
    }
}

export function* getTransportDetailFetchWatch() {
    yield takeEvery('transportDetail/getTransportDetailFetch', getTransportDetailFetch);
}

export type CreateTransportDetailPayload = {
    transport: TransportType;
    orderId?: number;
};

const createTransportDetailFetchRequest = async (payload: CreateTransportDetailPayload) => {
    const endpoint = payload.orderId
        ? `api/secure/orders/${payload.orderId}/transports`
        : 'api/secure/transports';
    const res = await api('post', endpoint, payload.transport);
    return res.data as TransportDetailResType;
};

function* createTransportDetailFetch(action: { type: string; payload: CreateTransportDetailPayload }) {
    try {
        const response: TransportDetailResType = yield call((): Promise<TransportDetailResType> =>
            createTransportDetailFetchRequest(action.payload)
        );
        yield put(
            createTransportDetailFetchSuccess({
                ...response,
                messageSuccess: { title: translate('transports.message.transportCreated') },
            })
        );
    } catch (error) {
        yield put(createTransportDetailFetchFailure({ error }));
    }
}

export function* createTransportDetailFetchWatch() {
    yield takeEvery('transportDetail/createTransportDetailFetch', createTransportDetailFetch);
}

const updateTransportDetailFetchRequest = async (params: { idTransport: string; data: TransportType }) => {
    const res = await api('put', `api/secure/transports/${params.idTransport}`, params.data);
    return res.data;
};

function* updateTransportDetailFetch(action: {
    type: string;
    payload: { idTransport: string; data: TransportType; callback?: (succes: boolean) => void };
}) {
    try {
        const response: TransportDetailResType = yield call((): Promise<TransportDetailResType> =>
            updateTransportDetailFetchRequest(action.payload)
        );
        yield put(
            updateTransportDetailFetchSuccess({
                ...response,
                messageSuccess: { title: translate('transports.message.transportUpdate') },
            })
        );
        if (action.payload.callback) {
            action.payload.callback(true);
        }
    } catch (error) {
        yield put(updateTransportDetailFetchFailure({ error }));
        if (action.payload.callback) {
            action.payload.callback(false);
        }
    }
}

export function* updateTransportDetailFetchWatch() {
    yield takeEvery('transportDetail/updateTransportDetailFetch', updateTransportDetailFetch);
}

const getTransportDetailChangelogFetchRequest = async (idTransport: string) => {
    const res = await api('get', withArchiveMode(`api/secure/transports/changelog/${idTransport}`));
    return res.data;
};

function* getTransportDetailChangelogFetch(action: { type: string; payload: string }) {
    try {
        const response: TransportDetailResType = yield call((): Promise<TransportDetailResType> =>
            getTransportDetailChangelogFetchRequest(action.payload)
        );
        yield put(getTransportDetailChangelogFetchSuccess(response));
    } catch (error) {
        yield put(getTransportDetailChangelogFetchFailure({ error }));
    }
}

export function* getTransportDetailChangelogFetchWatch() {
    yield takeEvery('transportDetail/getTransportDetailChangelogFetch', getTransportDetailChangelogFetch);
}

const getTransportPackageTypesFetchRequest = async (shipper?: string): Promise<ConstantsCodeType[]> => {
    const query = shipper ? `?shipper=${encodeURIComponent(shipper)}` : '';
    const res = await api('get', `api/secure/transports/package-types${query}`);
    const rows: TransportPackageTypeApiRow[] = Array.isArray(res.data?.data)
        ? res.data.data
        : Array.isArray(res.data)
          ? res.data
          : [];
    return rows
        .filter((row) => row.ACTIVE !== 0)
        .filter((row) => !shipper || shipper.toUpperCase() !== 'GEIS' || Boolean(row.GEIS_PACKING_TYPE_CODE))
        .map((row) => ({
            label: row.NAME,
            value: row.SSRC_CODE,
        }));
};

function* getTransportPackageTypesFetch(action: { type: string; payload?: string }) {
    try {
        const codes: ConstantsCodeType[] = yield call((): Promise<ConstantsCodeType[]> =>
            getTransportPackageTypesFetchRequest(action.payload)
        );
        yield put(getTransportPackageTypesFetchSuccess(codes));
    } catch {
        // Endpoint may not exist yet — keep UI usable with empty options.
        yield put(getTransportPackageTypesFetchFailure());
    }
}

export function* getTransportPackageTypesFetchWatch() {
    yield takeLatest('transportDetail/getTransportPackageTypesFetch', getTransportPackageTypesFetch);
}

const orderTransportShipmentFetchRequest = async (idTransport: string, force?: boolean) => {
    const res = await api('post', `api/secure/transports/${idTransport}/order-shipment`, force ? { force: true } : {});
    return res.data;
};

function* orderTransportShipmentFetch(action: { type: string; payload: { id: string; force?: boolean } }) {
    try {
        const response: TransportDetailResType = yield call((): Promise<TransportDetailResType> =>
            orderTransportShipmentFetchRequest(action.payload.id, action.payload.force)
        );
        yield put(
            orderTransportShipmentFetchSuccess({
                ...response,
                messageSuccess: { title: translate('transports.message.orderShipmentSuccess') },
            })
        );
        // Refresh detail after ordering
        yield put({ type: 'transportDetail/getTransportDetailFetch', payload: action.payload.id });
    } catch (error) {
        yield put(orderTransportShipmentFetchFailure({ error }));
        // transport-ms / core already set STATUS=incorrect + INCORRECT_MESSAGE — refresh detail
        yield put({ type: 'transportDetail/getTransportDetailFetch', payload: action.payload.id });
    }
}

export function* orderTransportShipmentFetchWatch() {
    yield takeEvery('transportDetail/orderTransportShipmentFetch', orderTransportShipmentFetch);
}

const getTransportApiCallsFetchRequest = async (idTransport: string) => {
    const res = await api('get', withArchiveMode(`api/secure/transports/${idTransport}/api-calls`));
    return res.data?.data ?? res.data ?? [];
};

function* getTransportApiCallsFetch(action: { type: string; payload: string }) {
    try {
        const response: TransportApiCallType[] = yield call((): Promise<TransportApiCallType[]> =>
            getTransportApiCallsFetchRequest(action.payload)
        );
        yield put(getTransportApiCallsFetchSuccess(response));
    } catch (error) {
        yield put(getTransportApiCallsFetchFailure());
    }
}

export function* getTransportApiCallsFetchWatch() {
    yield takeEvery('transportDetail/getTransportApiCallsFetch', getTransportApiCallsFetch);
}

const getTransportEventsFetchRequest = async (idTransport: string) => {
    const res = await api('get', withArchiveMode(`api/secure/transports/${idTransport}/events`));
    return res.data?.data ?? res.data ?? [];
};

function* getTransportEventsFetch(action: { type: string; payload: string }) {
    try {
        const response: TransportEventType[] = yield call((): Promise<TransportEventType[]> =>
            getTransportEventsFetchRequest(action.payload)
        );
        yield put(getTransportEventsFetchSuccess(response));
    } catch (error) {
        yield put(getTransportEventsFetchFailure());
    }
}

export function* getTransportEventsFetchWatch() {
    yield takeEvery('transportDetail/getTransportEventsFetch', getTransportEventsFetch);
}

const getTransportPickupDateFetchRequest = async (params: { shipper: string; hint?: string | null }) => {
    const query = new URLSearchParams({ shipper: params.shipper });
    if (params.hint) {
        query.set('hint', params.hint);
    }
    const res = await api('get', `api/secure/transports/pickup-date?${query.toString()}`);
    return res.data?.data?.pickupDate ?? res.data?.pickupDate ?? null;
};

function* getTransportPickupDateFetch(action: { type: string; payload: PickupDatePayload }) {
    try {
        const pickupDate: string | null = yield call((): Promise<string | null> =>
            getTransportPickupDateFetchRequest(action.payload)
        );
        action.payload.callback(pickupDate);
    } catch (_error) {
        action.payload.callback(null);
    }
}

export function* getTransportPickupDateFetchWatch() {
    yield takeLatest('transportDetail/getTransportPickupDateFetch', getTransportPickupDateFetch);
}

export default function* transportDetailSagas() {
    yield all([
        getTransportDetailFetchWatch(),
        createTransportDetailFetchWatch(),
        updateTransportDetailFetchWatch(),
        getTransportDetailChangelogFetchWatch(),
        getTransportPackageTypesFetchWatch(),
        orderTransportShipmentFetchWatch(),
        getTransportApiCallsFetchWatch(),
        getTransportEventsFetchWatch(),
        getTransportPickupDateFetchWatch(),
    ]);
}
